import React, { useEffect, useState } from 'react';
import { useI18n } from '@whizz/react-i18n';
import styled from 'styled-components';
import type { AverageReportType, GroupType } from '../../pages/types';

import { Button, ButtonIcon, Tooltip } from '../../components';
import { ButtonGroup } from '../../components/button/Button.styled';
import { useModal } from '../useModal';
import { SchoolReportFilterModal } from '../../global';
import { GLOBAL_ICONS } from '../../util/vars';
import type { SchoolReportFilterModalProps } from '../../global/modals/SchoolReportFilterModal';
import { useLocalStorage } from 'react-use';

const DEFAULT_SHOW_OTHERS_DATA = false;

export const useSchoolFilterSelector = (values:  (GroupType & AverageReportType)[]): [
  React.ReactElement,
  number | null,
  boolean,
  string,
  (clearShowOthersData: boolean) => void,
  React.Dispatch<React.SetStateAction<boolean>>,
  React.Dispatch<React.SetStateAction<boolean | undefined>>,
  (selectedClass: GroupType & AverageReportType, show: boolean) => void,
] => {
  const { translate } = useI18n();
  const [ value, setValue ] = useState<number | null>(null);
  const [ filterLabel, setFilterLabel ] = useState('');
  const [ showOthersData, setShowOthersData ] = useState(false);
  const [
    lastSessionShowOthersData,
    setLastSessionShowOthersData,
  ] = useLocalStorage('last_session_show_other_groups_data', DEFAULT_SHOW_OTHERS_DATA);

  const resetValue = (clearShowOthersData = true) => {
    setValue(null);
    setFilterLabel('');

    if (!clearShowOthersData) {
      return;
    }

    setShowOthersData(DEFAULT_SHOW_OTHERS_DATA);
    setLastSessionShowOthersData(DEFAULT_SHOW_OTHERS_DATA);
  };

  const handleSubmit = (selectedClass: GroupType & AverageReportType, show: boolean) => {
    setShowOthersData(show);
    setLastSessionShowOthersData(show);

    if (null == selectedClass) {
      setValue(null);
      setFilterLabel('');

      return;
    }

    setValue(selectedClass.id);
    setFilterLabel(selectedClass.fullName);
  };

  useEffect(() => {
    if (null == lastSessionShowOthersData) {
      return;
    }

    setShowOthersData(lastSessionShowOthersData);
  }, [ lastSessionShowOthersData ]);

  const [ modal, toggle, setProps ] = useModal((props: SchoolReportFilterModalProps) => (
    <SchoolReportFilterModal {...props} />
  ), {
    size: 'lg',
    disableHeader: true,
    values: [],
    filteredId: value,
    onSubmit: handleSubmit,
    resetFilter: resetValue,
  });

  const buttonComponent = (
    <>
      {modal}
      {value ? (
        <ButtonGroup>
          <CustomFilterButton
            color={'primary'}
            highlight
            onClick={() => {
              setProps((prev) => ({
                ...prev,
                values: values,
                filteredId: value,
                show: showOthersData,
              }));
              toggle(true);
            }}
          >
            {filterLabel}
          </CustomFilterButton>
          <Tooltip
            tooltipMessage={translate('pages.REPORTS.FILTERS.CLEAR')}
          >
            <CustomFilterButtonIcon
              size={'sm'}
              color={'primary'}
              isOutline
              highlight
              icon={GLOBAL_ICONS.close}
              onClick={() => {
                setValue(null);
                setFilterLabel('');
              }}
            />
          </Tooltip>
        </ButtonGroup>
      ) : (
        <CustomFilterButton
          disabled={values.length === 0}
          color={'primary'}
          isOutline
          onClick={() => {
            setProps((prev) => ({
              ...prev,
              values: values,
              filteredId: null,
            }));
            toggle(true);
          }}
        >
          {translate('pages.REPORTS.FILTERS.PLACEHOLDER')}
        </CustomFilterButton>
      )}
    </>
  );

  return [
    buttonComponent,
    value,
    // If no filters selected, show others data always
    null != value ? showOthersData : true,
    filterLabel,
    resetValue,
    setShowOthersData,
    setLastSessionShowOthersData,
    handleSubmit,
  ];
};

const CustomFilterButton = styled(Button)`
  [data-icon-filter="true"] {
    path {
      &:first-child {
        fill: ${(props) => props.theme.colors.header} !important;
      }
      &:last-child {
        fill: #1C335A !important;
      }
    }
  }
  border-radius: ${(props) => props.theme.radius.input};
  ${(props) => props.highlight && 'border-width: 3px;'}
`;

const CustomFilterButtonIcon = styled(ButtonIcon)`
  ${(props) => props.highlight && 'border-width: 3px;'}
`;
